import React from 'react';
import { useTheme, Grid, Tooltip, TextField, Button, Stack, Box, Checkbox, Typography, FormControlLabel, Modal, CircularProgress } from '@mui/material';
import { Controller, SubmitHandler, useForm, FormProvider } from 'react-hook-form';
import { EditMode } from '../../../../data/enums';
import { useAmaV2Context } from 'context/ama-v2-provider';
import { AmaV2Client } from 'context/ama-v2-provider/ama-v2.types';

interface IFormInputs {
  client_id: string;
  display_name: string;
  description: string;
  notes: string;
  is_active: boolean;
  zip_5: string;
  notification_recipients: string;
  optout_gender: boolean;
  optout_race: boolean;
  optout_student_of_color: boolean;
  optout_faith: boolean;
  optout_im_data: boolean;
  optout_exclude_test_score: boolean;
  extra_variable_1: string;
  extra_variable_2: string;
  extra_variable_3: string;
  extra_variable_4: string;
  extra_variable_5: string;
  extra_variable_6: string;
  extra_variable_7: string;
  extra_variable_8: string;
  extra_variable_9: string;
  extra_variable_10: string;
  distance_band_1: string;
  distance_band_2: string;
  distance_band_3: string;
  distance_band_4: string;
  distance_band_5: string;
  distance_band_6: string;
  distance_band_7: string;
}

interface IClientAddUpdateProps {
  mode: EditMode;
  setEditMode: React.Dispatch<React.SetStateAction<EditMode>>;
};

export default function ClientAddUpdate(props: IClientAddUpdateProps) {

  const theme = useTheme();  
  const { clientsState, setSelectedClient, addClient, updateClient, clearClientsError } = useAmaV2Context();
  const formProps = useForm<IFormInputs>({ defaultValues: getDefaultFormValues(clientsState.selectedClient) });

  // Log client id when component loads into view
  // React.useEffect(() => {
  //   console.log('ClientAddUpdate mounted');
  //   const id = clientsState.selectedClient?.client_id ?? '(none)';
  //   console.log(`ClientAddUpdate.mount :: client_id=${id}`);
  // }, []);

  const onSubmit: SubmitHandler<IFormInputs> = async (data: IFormInputs) => {
    
    const trimOrEmpty = (v: string) => (v === undefined || v === null) ? '' : v.trim();
    const toNullable = (v: string) => (v === undefined || v === null || v.trim() === '') ? null : v.trim();
    const basePayload: any = {
      display_name: trimOrEmpty(data.display_name), // required upstream
      description: trimOrEmpty(data.description),   // allow clearing
      notes: trimOrEmpty(data.notes),               // allow clearing
      is_active: !!data.is_active,                  // force boolean
      zip_5: trimOrEmpty(data.zip_5),               // allow clearing
      notification_recipients: trimOrEmpty(data.notification_recipients), // allow clearing
      optout_flag: {
        gender: !!data.optout_gender,
        race: !!data.optout_race,
        student_of_color: !!data.optout_student_of_color,
        faith: !!data.optout_faith,
        im_data: !!data.optout_im_data,
        exclude_test_score: !!data.optout_exclude_test_score,
      },
      extra_variable_names: [
        toNullable(data.extra_variable_1),
        toNullable(data.extra_variable_2),
        toNullable(data.extra_variable_3),
        toNullable(data.extra_variable_4),
        toNullable(data.extra_variable_5),
        toNullable(data.extra_variable_6),
        toNullable(data.extra_variable_7),
        toNullable(data.extra_variable_8),
        toNullable(data.extra_variable_9),
        toNullable(data.extra_variable_10),
      ],
      distance_bands: [
        toNullable(data.distance_band_1),
        toNullable(data.distance_band_2),
        toNullable(data.distance_band_3),
        toNullable(data.distance_band_4),
        toNullable(data.distance_band_5),
        toNullable(data.distance_band_6),
        toNullable(data.distance_band_7),
      ],
    };

    let success = false;
    if (props.mode === EditMode.Add) {
      success = await addClient({ client_id: data.client_id.trim(), ...basePayload });
    } else if (props.mode === EditMode.Edit && clientsState.selectedClient) {
      console.log('ClientAddUpdate.onSubmit :: update payload', { id: clientsState.selectedClient.client_id, payload: basePayload });
      success = await updateClient(clientsState.selectedClient.client_id, basePayload);
    }

    if (success) {
      // Return to list view
      setSelectedClient(null);
      props.setEditMode(EditMode.View);
    } else {
      console.error('ClientAddUpdate.onSubmit :: save failed');
    }
  };

  const handleCancelButtonClick = () => {
    setSelectedClient(null);
    props.setEditMode(EditMode.View);    
  };

  const handleModalOkButtonClick = () => {
    clearClientsError();
  };

  // const logFormErrors = () => {
  //   console.log(`ClientAddUpdate.DEBUG :: form errors: ${formProps.formState.errors}`);
  // }

  return (
    <Box sx={{ width: '100%', height: '100%', overflow: 'auto' }}>
      <Box id="inner" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minWidth: '600px' }}>
        <FormProvider {...formProps}>
          <form 
            noValidate 
            onSubmit={formProps.handleSubmit(onSubmit)}>
            
            {/* Loading overlay driven solely by clientsState.status */}
            {(clientsState.status === 'loading') && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  background: 'rgba(255, 255, 255, 0.6)',
                  zIndex: 999,
                }}>
                <CircularProgress />           
              </div>
            )}

            <Stack spacing={2} sx={{ width: '600px', margin: 0, padding: 0, mb: 10 }}>

              {/* Section header / IsActive checkbox */}  
              <Grid 
                container 
                alignItems="center"
                sx={{ height: 35 }}>              
                <Grid item xs={6}>
                  <Typography component="h6" variant="h6" sx={{ color: theme.palette.text.secondary, fontWeight: 'bold' }}>
                    Client Details
                  </Typography>
                </Grid>
                <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <FormControlLabel
                    label="Active"
                    control={
                      <Controller
                        name="is_active"
                        control={formProps.control}
                        render={({ field }) => (
                          <Checkbox
                            color="primary"
                            checked={field.value}
                            onChange={(e, checked) => field.onChange(checked)}
                          />
                        )}
                      />
                    }
                  />
                </Grid>
              </Grid>

              {/* ClientId */}          
              <TextField 
                label='Client ID'
                type='text'
                disabled={props.mode === EditMode.Edit}
                error={Boolean(formProps.formState.errors.client_id)}
                helperText={formProps.formState.errors.client_id?.message}
                {...formProps.register(
                  'client_id', 
                  { 
                    required: 'Client ID is required',
                    pattern: 
                    {
                      value: /^[a-z0-9_]+$/,
                      message: 'Client ID can only contain lowercase letters, numbers, and underscores.' 
                    }
                  })
                }
              />

              {/* DisplayName */}
              <TextField 
                label='Client display Name'
                type='text'
                error={Boolean(formProps.formState.errors.display_name)}
                helperText={formProps.formState.errors.display_name?.message}
                {...formProps.register(
                  'display_name', 
                  { required: 'Display Name is required' })
                }
              />

              {/* Zip Code */}
              <TextField 
                label='ZIP Code'
                type='text'
                inputProps={{ maxLength: 5 }}
                helperText={formProps.formState.errors.zip_5?.message}
                error={Boolean(formProps.formState.errors.zip_5)}
                {...formProps.register('zip_5', {
                  required: 'ZIP code is required',
                  pattern: {
                    value: /^\d{5}$/,
                    message: 'ZIP must be exactly 5 digits'
                  }
                })}
              />              

              {/* Description */}
              <TextField 
                label='Description (optional)'
                type='text'
                helperText={formProps.formState.errors.description?.message}
                {...formProps.register('description')}
              />
              {/* Notes */}
              <TextField 
                label='Notes (optional)'
                type='text'
                multiline
                minRows={2}
                helperText={formProps.formState.errors.notes?.message}
                error={Boolean(formProps.formState.errors.notes)}
                {...formProps.register('notes')}
              />

              {/* NotificationRecipients */}
              <TextField 
                label='Notification recipients (optional csv string)'
                type='text'
                helperText={formProps.formState.errors.notification_recipients?.message}
                error={Boolean(formProps.formState.errors.notification_recipients)}
                {...formProps.register(
                  'notification_recipients',
                  {
                    pattern: 
                    {
                      value: /^$|^(\s*[\w.-]+@[a-zA-Z\d.-]+\.[a-zA-Z]{2,}\s*)(,\s*[\w.-]+@[a-zA-Z\d.-]+\.[a-zA-Z]{2,}\s*)*$/,
                      message: 'Must be comma separated list of email addresses.' 
                    }
                  }
                )}
              />

              {/* Optout Flags */}
              <Typography component="h6" variant="h6" sx={{ color: theme.palette.text.secondary, mt: 1, fontWeight: 'bold' }}>
                Optout Flags
              </Typography>
              <Grid container spacing={1}>
                {(['gender', 'race', 'student_of_color', 'faith', 'im_data', 'exclude_test_score'] as const).map((flag) => (
                  <Grid item xs={6} key={flag}>
                    <FormControlLabel
                      label={
                        flag === 'faith'
                          ? 'Faith Based'
                          : flag === 'im_data'
                            ? 'IM Data (select if FM-Only)'
                            : flag.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
                      }
                      control={
                        <Controller
                          name={`optout_${flag}` as keyof IFormInputs}
                          control={formProps.control}
                          render={({ field }) => (
                            <Checkbox
                              color="primary"
                              checked={!!field.value}
                              onChange={(e, checked) => field.onChange(checked)}
                            />
                          )}
                        />
                      }
                    />
                  </Grid>
                ))}
              </Grid>

              {/* Extra Variables */}
              <Typography component="h6" variant="h6" sx={{ color: theme.palette.text.secondary, mt: 1, fontWeight: 'bold' }}>
                Extra Variables
              </Typography>
              {([1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const).map((n) => (
                <TextField
                  key={n}
                  label={`Extra Variable ${n}`}
                  type='text'
                  inputProps={{ maxLength: 100 }}
                  {...formProps.register(`extra_variable_${n}` as keyof IFormInputs)}
                />
              ))}

              {/* Distance Bands */}
              <Typography component="h6" variant="h6" sx={{ color: theme.palette.text.secondary, mt: 1, fontWeight: 'bold' }}>
                Distance Bands
              </Typography>
              {([1, 2, 3, 4, 5, 6, 7] as const).map((n) => (
                <TextField
                  key={n}
                  label={`Distance Band ${n}`}
                  type='text'
                  inputProps={{ maxLength: 100 }}
                  {...formProps.register(`distance_band_${n}` as keyof IFormInputs)}
                />
              ))}

              {/* Save/Cancel buttons */}
              <Stack spacing={2} direction="row" alignItems="center" justifyContent="flex-end" width={600}>
                <Button type='submit' variant='contained' color='primary'>Save</Button>
                <Button variant='outlined' color='primary' onClick={handleCancelButtonClick}>Cancel</Button>
                {/* <Button variant='outlined' color='primary' onClick={() => logFormErrors()}>Debug</Button> */}
              </Stack>   

            </Stack>
          </form>

          {/* Remove when dev complete */}
          {/* <DevTool control={formProps.control}/> */}

        </FormProvider>

        {/* Error modal */}
        <Modal
          open={Boolean(clientsState.error)}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description">
          <Box sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.grey[300]}`,
            boxShadow: 24,
            p: 4,}}>
            <Typography id="modal-modal-title" variant="h6" component="h2">
              Error
            </Typography>
            <Typography id="modal-modal-description" sx={{ mt: 2, mb: 4 }}>
              {clientsState.error}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button variant="contained" color="primary" onClick={handleModalOkButtonClick}>Ok</Button>
            </Box>
          </Box>
        </Modal>

      </Box>
    </Box>
  );
}

const getDefaultFormValues = (client: AmaV2Client | null): IFormInputs => {
  
  const ev = (arr: (string | null)[] | undefined, i: number) => arr?.[i] ?? '';
  const db = (arr: (string | null)[] | undefined, i: number) => arr?.[i] ?? '';

  if (client) {    
    return {
      client_id: client.client_id,
      display_name: client.display_name || '',
      description: client.description || '',
      notes: client.notes || '',
      is_active: client.is_active,
      zip_5: client.zip_5 || '',
      notification_recipients: Array.isArray(client.notification_recipients) ? client.notification_recipients.join(', ') : (client.notification_recipients || ''),
      optout_gender: client.optout_flag?.gender ?? false,
      optout_race: client.optout_flag?.race ?? false,
      optout_student_of_color: client.optout_flag?.student_of_color ?? false,
      optout_faith: client.optout_flag?.faith ?? false,
      optout_im_data: client.optout_flag?.im_data ?? false,
      optout_exclude_test_score: client.optout_flag?.exclude_test_score ?? false,
      extra_variable_1: ev(client.extra_variable_names, 0),
      extra_variable_2: ev(client.extra_variable_names, 1),
      extra_variable_3: ev(client.extra_variable_names, 2),
      extra_variable_4: ev(client.extra_variable_names, 3),
      extra_variable_5: ev(client.extra_variable_names, 4),
      extra_variable_6: ev(client.extra_variable_names, 5),
      extra_variable_7: ev(client.extra_variable_names, 6),
      extra_variable_8: ev(client.extra_variable_names, 7),
      extra_variable_9: ev(client.extra_variable_names, 8),
      extra_variable_10: ev(client.extra_variable_names, 9),
      distance_band_1: db(client.distance_bands, 0),
      distance_band_2: db(client.distance_bands, 1),
      distance_band_3: db(client.distance_bands, 2),
      distance_band_4: db(client.distance_bands, 3),
      distance_band_5: db(client.distance_bands, 4),
      distance_band_6: db(client.distance_bands, 5),
      distance_band_7: db(client.distance_bands, 6),
    }
  }

  return {
    client_id: '',
    display_name: '',
    description: '',
    notes: '',
    is_active: true,
    zip_5: '',
    notification_recipients: '',
    optout_gender: false,
    optout_race: false,
    optout_student_of_color: false,
    optout_faith: false,
    optout_im_data: false,
    optout_exclude_test_score: false,
    extra_variable_1: '',
    extra_variable_2: '',
    extra_variable_3: '',
    extra_variable_4: '',
    extra_variable_5: '',
    extra_variable_6: '',
    extra_variable_7: '',
    extra_variable_8: '',
    extra_variable_9: '',
    extra_variable_10: '',
    distance_band_1: '',
    distance_band_2: '',
    distance_band_3: '',
    distance_band_4: '',
    distance_band_5: '',
    distance_band_6: '',
    distance_band_7: '',
  };
};











// import ManifestBuilder from '../manifest-builder';
// import { useAmaWorkflowContext } from '../../../../context/ama-workflow-provider';

// interface IFormInputs {
//   ClientId: string;
//   DisplayName: string;
//   Description: string;
//   IsActive: boolean;
//   IsStrictMode: boolean;
//   IsAutoStart: boolean;
//   IsCensusHistoricAvailable: boolean;
//   CensusCurrentYearSnapshot: string;
//   SilverGlueJobName: string;
//   GoldGlueJobName: string;
//   NotificationRecipients: string;
//   ManifestItems: Array<IManifestItem>;
// }


// const { addClient, updateClient, selectedClient, setSelectedClient, clientsError, clearClientErrors, clientsLoading } = useAmaWorkflowContext();